import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { Operation } from 'fast-json-patch';

import { useCapabilityCheck } from '@hcs/authz';
import { Button, Skeleton, TextButton } from '@hcs/design-system';
import { Checkbox } from '@hcs/design-system';
import { ActionButtons } from '@hcs/design-system';
import { Anchor } from '@hcs/design-system';
import { Ledger } from '@hcs/design-system';
import { NullState } from '@hcs/design-system';
import { ExperienceFlag } from '@hcs/experience-flags';
import { useScreenSize } from '@hcs/hooks';
import { useNavigationContext } from '@hcs/navigation';
import { getPropertyStateFieldValue } from '@hcs/property-state';
import {
  MeaningfulEventTypes,
  PropertyStateArgsCore,
  PropertyStateEditCallbackArgs,
  PropertyStateFields,
} from '@hcs/types';
import { ReportFeatures, ReportId, ValuationMethod } from '@hcs/types';
import { formatMoney } from '@hcs/utils';
import { combineUseQueryResult } from '@hcs/utils';

import {
  BadgeProps,
  SubjectDetailsTable,
  SubjectDetailsTableTheme,
} from '../../components/SubjectDetailsTable/SubjectDetailsTable';
import { UserPropertyAdjustedBadge } from '../../components-deprecated';
import { SELECTED_VALUE_PATH } from '../../constants/subjectDetails.constants';
import {
  useDocumentPatch,
  useReportPermissions,
  useSubjectDocument,
  useSubjectValueDocument,
} from '../../hooks';
import { usePreviewAdjustedAvm } from '../../hooks/usePreviewAdjustedAvm';
import { useReportConfig } from '../../hooks/useReportConfig';
import { useRevertSubjectPropertyAdjustments } from '../../hooks/useRevertSubjectPropertyAdjustments';
import { ReportFeaturesSupported } from '../ReportFeaturesSupported';

import { dataHcEventSectionEditSubjectDetails } from './reportSubjectDetailsTable.constants';

import styles from './ReportSubjectDetailsTable.module.css';

const DEFAULT_COLS: PropertyStateFields[][] = [
  [
    PropertyStateFields.livingArea,
    PropertyStateFields.livingAreaAboveGrade,
    PropertyStateFields.livingAreaBelowGrade,
    PropertyStateFields.lotSize,
    PropertyStateFields.bedrooms,
    PropertyStateFields.bathrooms,
    PropertyStateFields.stories,
    PropertyStateFields.propertyType,
    PropertyStateFields.ownerOccupied,
    PropertyStateFields.yearBuilt,
    PropertyStateFields.basementHas,
    PropertyStateFields.pool,
    PropertyStateFields.daysOnMarketCumulative,
    PropertyStateFields.apn,
    PropertyStateFields.concessions,
    PropertyStateFields.concessionSellerAmount,
    PropertyStateFields.concessionSellerDescription,
  ],
  [
    PropertyStateFields.garageType,
    PropertyStateFields.garageSpaces,
    PropertyStateFields.condition,
    PropertyStateFields.subdivisionName,
    PropertyStateFields.zoning,
    PropertyStateFields.associationName,
    PropertyStateFields.associationFee,
    PropertyStateFields.associationFeeFrequency,
    PropertyStateFields.associationFeeIncludes,
    PropertyStateFields.taxYear,
    PropertyStateFields.taxAmount,
    PropertyStateFields.county,
    PropertyStateFields.concessionsAmount,
    PropertyStateFields.concessionBuyerAmount,
    PropertyStateFields.concessionBuyerDescription,
    PropertyStateFields.concessionsComments,
  ],
];

interface Props {
  reportId: ReportId;
  className?: string;
  theme?: Partial<SubjectDetailsTableTheme>;
  propertyFieldsTables?: PropertyStateFields[][];
  useCompactRows?: boolean;
  allowAdjustFields: boolean;
}
const dataHcName = 'subject-details-table';

/* Higher-order component that provides state and data to SubjectDetailsTable */
export const ReportSubjectDetailsTable = ({
  reportId,
  theme,
  propertyFieldsTables = DEFAULT_COLS,
  className,
  useCompactRows = false,
  allowAdjustFields,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isPhoneWidth: scrollIntoViewOnAdjustSave } = useScreenSize();
  const { data: hasBpoReportSignCapability } =
    useCapabilityCheck('bpo-report-sign');
  const { makePexpReportLink } = useNavigationContext();
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [adjustmentValuePreview, setAdjustmentValuePreview] = useState<
    number | null
  >(null);
  const [operations, setOperations] = useState<Operation[]>([]);
  const [valuationMethod, setValuationMethod] = useState<
    ValuationMethod | undefined
  >();
  const documentPatchMutation = useDocumentPatch(reportId);
  const { mutate: previewAdjustedAvmMutation } = usePreviewAdjustedAvm();
  const subjectDocumentQuery = useSubjectDocument(reportId);
  const reportConfigQuery = useReportConfig(reportId);
  const subjectValueDocumentQuery = useSubjectValueDocument(reportId);
  const reportPermissionsQuery = useReportPermissions(reportId);
  const combinedQuery = combineUseQueryResult([
    subjectDocumentQuery,
    reportConfigQuery,
    subjectValueDocumentQuery,
    reportPermissionsQuery,
  ]);
  const revertSubjectPropertyAdjustments =
    useRevertSubjectPropertyAdjustments(reportId);
  const { data: reportPermissions } = reportPermissionsQuery;
  const { data: subjectDocument } = subjectDocumentQuery;
  const { data: reportConfig } = reportConfigQuery;
  const { data: subjectValueDocument } = subjectValueDocumentQuery;
  const adjustments = subjectDocument?.userPropertyAdjustments?.adjustments;
  const containsSavedUserAdjustedValues =
    adjustments && Object.keys(adjustments).length > 0;
  const activeReportLink = makePexpReportLink && makePexpReportLink(reportId);

  useEffect(() => {
    if (operations.length) {
      previewAdjustedAvmMutation(
        { reportId, operations },
        {
          onSuccess: (data) => {
            setAdjustmentValuePreview(data.value.value || null);
          },
        },
      );
    }
  }, [operations, reportId, previewAdjustedAvmMutation]);

  if (combinedQuery.isLoading) {
    return (
      <Skeleton
        dataHcName={`${dataHcName}-skeleton`}
        type="text"
        repeat={10}
        paddingTop={10}
        height={27}
      />
    );
  }

  const subjectSchema = subjectDocument?.data;

  if (combinedQuery.isError || !subjectSchema) {
    return (
      <NullState
        dataHcName={`${dataHcName}-error`}
        title="Error Loading Subject"
      />
    );
  }

  const handleChange = (
    args: PropertyStateEditCallbackArgs<PropertyStateFields>,
  ): void => {
    const operation: Operation = {
      path: `/data/propertyState${args.path}`,
      op: 'add',
      value: args.value,
    };

    const subjectDetailCurrentValue = getPropertyStateFieldValue(args.field, {
      propertyStateType: 'Core',
      propertyState: subjectSchema.propertyState,
    });

    const parsedSubjectDetailCurrentValue =
      // Handle original detail values that are floats
      typeof subjectDetailCurrentValue === 'number' &&
      args.field !== PropertyStateFields.bathrooms
        ? Math.round(subjectDetailCurrentValue || 0)
        : subjectDetailCurrentValue;

    if (parsedSubjectDetailCurrentValue === operation.value) {
      setOperations([...operations.filter((op) => op.path !== operation.path)]);
    } else if (parsedSubjectDetailCurrentValue !== operation.value) {
      setOperations([
        ...operations.filter((op) => op.path !== operation.path),
        operation,
      ]);
    }
  };

  const cancelAdjustments = () => {
    setOperations([]);
    setAdjustmentValuePreview(null);
    setIsAdjusting(false);
  };

  const patchAdjustments = () => {
    /**
     * If a user adjusts property details and selects to use their adjusted
     * value for the report, Report-API currently needs to process adjusted
     * operations first.
     *
     * This is because without first processing those adjusted operations,
     * our "adjusted value" will be null.
     *
     * Reference EXP-1840 for more details.
     */
    if (operations.length && valuationMethod && subjectValueDocument) {
      documentPatchMutation.mutate(
        {
          reportId,
          document: subjectDocument,
          operations: operations,
        },
        {
          onSettled: () => {
            documentPatchMutation.mutate({
              reportId,
              document: subjectValueDocument,
              operations: [
                {
                  op: 'replace',
                  path: SELECTED_VALUE_PATH,
                  value: valuationMethod,
                },
              ],
            });
          },
        },
      );
    } else {
      if (valuationMethod && subjectValueDocument) {
        documentPatchMutation.mutate({
          reportId,
          document: subjectValueDocument,
          operations: [
            {
              op: 'replace',
              path: SELECTED_VALUE_PATH,
              value: valuationMethod,
            },
          ],
        });
      }

      if (operations.length) {
        documentPatchMutation.mutate({
          reportId,
          document: subjectDocument,
          operations: operations,
        });
      }
    }

    setOperations([]);
    setAdjustmentValuePreview(null);
    setIsAdjusting(false);
    setValuationMethod(undefined);

    // Scroll page to place section at the top if on phone
    if (scrollIntoViewOnAdjustSave && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const hcAvm = subjectValueDocument?.data[ValuationMethod.Avm]?.value.value;
  const adjustedAvm =
    isAdjusting && adjustmentValuePreview !== null
      ? adjustmentValuePreview
      : subjectValueDocument?.data[ValuationMethod.Adjusted]?.value.value;
  const adjustedValueDiff =
    adjustedAvm != null && hcAvm != null ? adjustedAvm - hcAvm : undefined;
  const propertyStateArgs: PropertyStateArgsCore = {
    propertyStateType: 'Core',
    propertyState: subjectDocument.data.propertyState,
  };
  const selectValueDisabled =
    !containsSavedUserAdjustedValues && operations.length === 0;
  const selectValueChecked =
    valuationMethod === undefined
      ? subjectValueDocumentQuery.data?.data.selectedValue ===
        ValuationMethod.Adjusted
      : valuationMethod === ValuationMethod.Adjusted;
  const saveAdjustmentsDisabled = operations.length === 0 && !valuationMethod;

  const Badge: React.FC<BadgeProps> = ({ label, compField, Display }) => {
    const compFieldUserAdjustment =
      subjectDocument?.userPropertyAdjustments?.adjustments?.[compField];
    return (
      compFieldUserAdjustment &&
      compFieldUserAdjustment.currentValue !==
        compFieldUserAdjustment.sourceValue && (
        <UserPropertyAdjustedBadge
          dataHcName={`${dataHcName}-comp-field-user-adjustment`}
          label={label}
          value={
            <Display
              key={`subject-col-${compField}`}
              propertyStateArgs={{
                propertyStateType: 'flat',
                propertyState: {
                  [compField]: compFieldUserAdjustment.sourceValue,
                },
              }}
            />
          }
        />
      )
    );
  };

  return (
    <div
      data-hc-name={`${dataHcName}-container`}
      data-hc-event-section={
        isAdjusting ? dataHcEventSectionEditSubjectDetails : undefined
      }
      className={styles.SubjectDetailsTableContainer}
      ref={containerRef}
    >
      <ReportFeaturesSupported
        reportId={reportId}
        reportFeatures={[
          ReportFeatures.SubjectEditDetails,
          ReportFeatures.SubjectEditCondition,
        ]}
      >
        {reportPermissions?.isEditable && (
          <span
            data-hc-event-section={dataHcEventSectionEditSubjectDetails}
            className={classNames(styles.ActionBar, theme?.ActionBar)}
          >
            {!isAdjusting ? (
              allowAdjustFields ? (
                <Anchor
                  dataHcName={`${dataHcName}-adjust`}
                  className={classNames(styles.Control, theme?.Control)}
                  onClick={() => setIsAdjusting(!isAdjusting)}
                >
                  Adjust Fields
                </Anchor>
              ) : (
                activeReportLink && (
                  <Link to={activeReportLink}>
                    <TextButton
                      dataHcName={`${dataHcName}-adjust`}
                      className={classNames(styles.Control, theme?.Control)}
                      noPadding
                    >
                      Adjust Fields in Property Explorer
                    </TextButton>
                  </Link>
                )
              )
            ) : (
              <ExperienceFlag
                experienceFlagId="PEXP_REDESIGN"
                now={
                  <>
                    <Anchor
                      dataHcName={`${dataHcName}-cancel`}
                      className={classNames(styles.Control, theme?.Control)}
                      onClick={cancelAdjustments}
                    >
                      Cancel
                    </Anchor>
                    <Anchor
                      dataHcName={`${dataHcName}-save`}
                      className={classNames(styles.Control, theme?.Control)}
                      onClick={patchAdjustments}
                    >
                      Save
                    </Anchor>
                  </>
                }
                next={
                  <>
                    <Anchor
                      dataHcName={`${dataHcName}-cancel`}
                      className={classNames(styles.Control, theme?.Control)}
                      onClick={cancelAdjustments}
                    >
                      Cancel
                    </Anchor>
                    <Anchor
                      disabled={!containsSavedUserAdjustedValues}
                      aria-disabled={!containsSavedUserAdjustedValues}
                      dataHcName={`${dataHcName}-reset`}
                      className={classNames(styles.Control, theme?.Control)}
                      onClick={() =>
                        revertSubjectPropertyAdjustments(() => {
                          setOperations([]);
                          setAdjustmentValuePreview(null);
                          setIsAdjusting(false);
                          setValuationMethod(undefined);
                        })
                      }
                    >
                      Reset
                    </Anchor>
                  </>
                }
              />
            )}
          </span>
        )}
      </ReportFeaturesSupported>
      <SubjectDetailsTable
        propertyFieldsTables={propertyFieldsTables}
        dataHcName={dataHcName}
        className={className}
        theme={theme}
        isAdjusting={isAdjusting}
        useCompactRows={useCompactRows}
        Badge={Badge}
        canEditCondition={
          reportConfig?.reportFeaturesSupport[
            ReportFeatures.SubjectEditCondition
          ]
        }
        canEditDetails={
          reportConfig?.reportFeaturesSupport[ReportFeatures.SubjectEditDetails]
        }
        propertyStateArgs={propertyStateArgs}
        handleChange={handleChange}
      />
      <ExperienceFlag
        experienceFlagId="PEXP_REDESIGN"
        now={
          adjustedValueDiff ? (
            <div
              className={styles.AdjustedValueDiff}
              data-hc-name={`${dataHcName}-adjusted-difference`}
            >
              <div
                className={styles.AdjustedValueDiffLabel}
                data-hc-name={`${dataHcName}-adjusted-difference-label`}
              >
                Adjusted Value Difference
              </div>
              <div
                className={classNames(styles.AdjustedValueDiffValue, {
                  [styles.positive]: adjustedValueDiff > 0,
                  [styles.negative]: adjustedValueDiff < 0,
                })}
                data-hc-name={`${dataHcName}-adjusted-difference-value`}
              >
                {`${adjustedValueDiff > 0 ? '+' : ''}${formatMoney(
                  adjustedValueDiff,
                )}`}
              </div>
            </div>
          ) : null
        }
        next={
          <ReportFeaturesSupported
            reportId={reportId}
            reportFeatures={[
              ReportFeatures.SubjectEditDetails,
              ReportFeatures.SubjectEditCondition,
            ]}
          >
            {isAdjusting && (
              <div
                data-hc-name={`${dataHcName}-adjusting-summary`}
                className={styles.AdjustingSummary}
              >
                <div className={styles.Column}>
                  <Ledger
                    dataHcName={`${dataHcName}-adjustment-breakdown`}
                    items={[
                      {
                        arrayKey: 'hc-estimated-value',
                        label: 'HC Estimated Value',
                        value:
                          subjectValueDocument?.data[ValuationMethod.Avm]?.value
                            .value,
                        className: styles.LedgerItem,
                        highlightValue: useCompactRows,
                      },
                      {
                        arrayKey: 'adjusted-value-difference',
                        label: 'Adjusted Value Difference',
                        value:
                          adjustedAvm && hcAvm ? adjustedAvm - hcAvm : null,
                        className: classNames(styles.LedgerItem, {
                          [styles.positive]:
                            adjustedAvm && hcAvm && adjustedAvm > hcAvm,
                          [styles.negative]:
                            adjustedAvm && hcAvm && adjustedAvm < hcAvm,
                        }),
                      },
                    ]}
                    total={{
                      label: 'Adjusted Value',
                      value: adjustedAvm || hcAvm,
                    }}
                  />
                </div>
                <div className={styles.Column}>
                  <ActionButtons
                    className={styles.ActionButtons}
                    dataHcName={`${dataHcName}-action-buttons`}
                    noStretch
                  >
                    <Button
                      label="Save Adjustments"
                      dataHcName={`${dataHcName}-save`}
                      onClick={patchAdjustments}
                      disabled={saveAdjustmentsDisabled}
                      dataHcEventName={
                        saveAdjustmentsDisabled
                          ? undefined
                          : 'Saved Adjustments'
                      }
                      dataHcEventType={
                        saveAdjustmentsDisabled
                          ? undefined
                          : MeaningfulEventTypes.Goal
                      }
                    />
                    <Anchor
                      dataHcName={`${dataHcName}-cancel`}
                      className={classNames(styles.Control, theme?.Control)}
                      onClick={cancelAdjustments}
                    >
                      Cancel
                    </Anchor>
                    <Anchor
                      disabled={!containsSavedUserAdjustedValues}
                      aria-disabled={!containsSavedUserAdjustedValues}
                      dataHcName={`${dataHcName}-reset`}
                      className={classNames(styles.Control, theme?.Control)}
                      onClick={() =>
                        revertSubjectPropertyAdjustments(() => {
                          setOperations([]);
                          setAdjustmentValuePreview(null);
                          setIsAdjusting(false);
                          setValuationMethod(undefined);
                        })
                      }
                    >
                      Reset
                    </Anchor>
                  </ActionButtons>
                  <Checkbox
                    disabled={selectValueDisabled}
                    checked={selectValueChecked}
                    className={styles.Checkbox}
                    dataHcName={`${dataHcName}-apply-to-report`}
                    dataHcEventName={
                      selectValueDisabled
                        ? undefined
                        : selectValueChecked
                          ? 'Deselected Adjusted Avm'
                          : 'Selected Adjusted Avm'
                    }
                    dataHcEventType={
                      selectValueDisabled
                        ? undefined
                        : MeaningfulEventTypes.Goal
                    }
                    label="Use your adjusted value for report?"
                    detail={
                      !hasBpoReportSignCapability &&
                      "This value is based on adjustments that have been made to the property's core characteristics. It does not represent a broker price opinion (BPO) or a formal appraisal."
                    }
                    onChange={(v) => {
                      v
                        ? setValuationMethod(ValuationMethod.Adjusted)
                        : subjectValueDocumentQuery.data?.data.selectedValue ===
                            ValuationMethod.Adjusted
                          ? setValuationMethod(ValuationMethod.Avm)
                          : setValuationMethod(undefined);
                    }}
                  />
                </div>
              </div>
            )}
          </ReportFeaturesSupported>
        }
      />
    </div>
  );
};
